// Design System Checkbox Component
// Unified checkbox component

import React, { forwardRef } from 'react';
import { 
  Checkbox as MuiCheckbox,
  FormControlLabel,
  FormControl,
  FormHelperText,
  styled
} from '@mui/material';
import PropTypes from 'prop-types';
import { colorTokens, transitionTokens } from '../../tokens';

// Styled Checkbox with design system tokens
const StyledCheckbox = styled(MuiCheckbox)(({ theme }) => {
  const mode = theme.palette.mode;
  const colors = mode === 'dark' ? colorTokens.dark : colorTokens.light;
  
  return {
    color: colors.text.secondary,
    transition: transitionTokens.fast,
    
    '&.Mui-checked': {
      color: colors.primary.main,
    },
    
    '&:hover': {
      backgroundColor: `${colors.primary.main}14`,
    },
    
    '&.Mui-disabled': {
      color: colors.text.disabled,
    },
  };
});

/**
 * Checkbox - Unified checkbox component
 * 
 * @example
 * // Basic usage
 * <Checkbox 
 *   label="I agree to the terms" 
 *   checked={agreed} 
 *   onChange={setAgreed} 
 * />
 * 
 * // With helper text
 * <Checkbox 
 *   label="Subscribe to newsletter" 
 *   checked={subscribed} 
 *   onChange={setSubscribed}
 *   helperText="You can unsubscribe at any time"
 * />
 */
const Checkbox = forwardRef(({
  label,
  helperText,
  error,
  ...props
}, ref) => {
  const checkbox = <StyledCheckbox ref={ref} {...props} />;
  
  if (!label && !helperText) {
    return checkbox;
  }
  
  return (
    <FormControl error={Boolean(error)}>
      <FormControlLabel
        control={checkbox}
        label={label}
      />
      {helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
});

Checkbox.propTypes = {
  label: PropTypes.node,
  helperText: PropTypes.node,
  error: PropTypes.bool,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  // All other MUI Checkbox props are supported
};

Checkbox.displayName = 'Checkbox';

export default Checkbox;